import React, { useContext, useCallback, useMemo, CSSProperties, Fragment } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { makeStyles, createStyles, Theme, IconButton, Icon } from '@material-ui/core';
import { modeUIContext, selectDrawerMenuOpened, openDrawerMenu, closeDrawerMenu, selectControlPanelComponents } from '@moderepo/mode-ui-state';
import { Agent, AgentRole, DEFAULT_ORGANIZATION_ID } from '@moderepo/mode-internal-apis';
import { logoutAgent, ritzDataContext, selectLoggedInAgent, selectProjectAgent, selectProjectById, selectProjects }
    from '@moderepo/mode-data-state-ritz';
import { DrawerMenu, TopNavBar, deleteLocalStorageItem, capitalizeAll, DrawerMenuItem } from '@moderepo/mode-app-components';
import clsx from 'clsx';
import { getAgentRoleName } from '@moderepo/mode-app-components-internal';
import { TFunction } from 'i18next';
import { RouteHelper } from '../routes/RouteHelper';
import { useRitzTranslation } from '../../utils/customHooks';
import * as Constants from '../../utils/constants';
import { LanguageSelectorContainer } from '../language-selector/LanguageSelectorContainer';
import { SubRouteNavBarContainer } from './SubRouteNavBarContainer';
import { ProjectTypeHandlersManager } from '../../utils/ritzExtensionUtils';
import { PageInfoBarContainer } from '../misc';
import { getOrganization } from '../app/App';
import { NotAuthorized } from '..';
import { RitzDrawerMainMenuItem } from '../../utils/ritzUIExtensionUtils';



export const useLayoutStyle = makeStyles((theme: Theme) => {
    return createStyles({
        root: {
            width              : '100%',
            height             : '100%',
            overflow           : 'hidden',
            display            : 'grid',
            gridTemplateColumns: '0px auto 0px',      // Third column is reserved for right pane which we don't need yet so set to 0 for now.
            gridTemplateRows   : 'auto 1fr 0px',     // Third row is reserved for footer which we don't need yet so set to 0 for now.
            gridTemplateAreas  : `
                                    'menu topBar topBar'
                                    'menu main right'
                                    'menu footer footer'
                                `,
            gridGap                     : 0,
            [theme.breakpoints.up('md')]: {
                gridTemplateColumns: 'auto 1fr 0px',      // Third column is reserved for right pane which we don't need yet so set to 0 for now.
            },
        },

        drawerMenu: {
            gridArea      : 'menu',
            '& .app-title': {
                whiteSpace: 'normal',
                textAlign : 'center',
            },
        },

        topNavBar: {
            gridArea: 'topBar',
        },

        mainContent: {
            gridArea: 'main',
            overflow: 'auto',
        },

        pageInfoBar: {
            background: theme.palette.background.paper,
            padding   : theme.spacing(0.5),
        },

        drawerMenuToggler: {
            marginRight                 : theme.spacing(2),
            [theme.breakpoints.up('lg')]: {
                display: 'none',
            },
        },
    });
}, { name: 'Layout', index: 1 });


enum MenuItem {
    MY_ACCOUNT= 'my account',
    LOGOUT = 'logout'
}



/**
 * Helper function to check if a menuItem is visible. If a menuItem is not visible then we will not include in the list of menuItems
 * that will be passed to the drawer menu.
 */
const isMenuItemVisible = (menuItem: RitzDrawerMainMenuItem, agentRole: AgentRole | undefined): boolean => {
    // A menu item is visible if it is
    return !menuItem.hidden                                          // NOT marked as hidden
        && (!menuItem.adminOnly || agentRole === AgentRole.ADMIN)    // NOT for adminOnly unless it is adminOnly and the logged in agent is an admin.
        && (!menuItem.subMenuItems || menuItem.subMenuItems.some((subMenuItem) => {     // DOES NOT have subMenuItems but if it does, at least 1 of
            return isMenuItemVisible(subMenuItem, agentRole);                           // its' subMenuItem is visible
        }));
};



/**
 * Create a link for a menu item based on the provided path. Each menu item has link to some page in Ops Console e.g. Users, Home, Devices,
 * Entities page etc.... The menu item definition will contain the path to the page but they can be in different format. Some item has
 * the actually path to the page including the actual projectId, some has path but with {{projectId}} as placeholder which we need to
 * replace with the actual projectId, and some item only contain relative path to the project excluding projectId. Therefore, this function
 * is to take the "path" and fix it so that it can be used. There are 3 different paths that we support. The 3rd one is the one that is preferred
 * because that path doesn't require the admin to fill in "/project/{{projectId}}" which is a little more work"
 *
 *     1. "/projects/1234/users" - This path is EXACTLY what we need so we don't need to fix these paths
 *     2. "/projects/{{projectId}}/users" - For this kind of path, we need to replace the {{projectId}} placeholder with the actual projectId
 *     3. "/users" - For this path, we need to automatically prepend "/projects/1234".
 */
const normalizeProjectMenuItemLink = (
    path: string | undefined, projectId: number | undefined, routeHelper: RouteHelper, trans: TFunction,
): string => {
    if (path && projectId) {
        // First, look in the path and replace {{projectId}} placeholders with the current project's ID
        const pathWithProjectId = trans(path, { projectId: projectId ?? '' });

        // Get the path of the project's default landing page, "/projects/{{projectId}}"
        const defaultProjectLandingPage = routeHelper.getProjectHomePageUrl(projectId);

        // ALL project custom menu items' path must start with "/projects/{{projectId}}". If a path does not start with "/project/{projectId}}",
        // we will automatically fix it by the "/project/{projectId}}" to the beginning of the path.
        const fixedPath = !pathWithProjectId.startsWith(defaultProjectLandingPage)
            ? `${defaultProjectLandingPage}${pathWithProjectId.startsWith('/') ? '' : '/'}${pathWithProjectId}`
            : pathWithProjectId;

        return fixedPath;
    }

    return path ?? '/';
};



export interface LayoutProps extends React.PropsWithChildren<any> {
    // Custom className/style for this component root element
    readonly className?: string | undefined;
    readonly style?: CSSProperties | undefined;

    // Custom className/style for topNav component
    readonly topNavProps?: {
        readonly className?: string | undefined;
        readonly style?: CSSProperties | undefined;
    } | undefined;

    // Custom className/style for drawerMenu component
    readonly drawerMenuProps?: {
        readonly className?: string | undefined;
        readonly style?: CSSProperties | undefined;
    } | undefined;
}


export const Layout: React.FC<LayoutProps> = (props: LayoutProps) => {
    const navigate = useNavigate();
    const location = useLocation();
    const params = useParams();
    const projectId = params.projectId ? Number(params.projectId) : undefined;


    const { state:uiState, dispatch:uiDispatch } = useContext(modeUIContext);
    const { state:dataState, dispatch:dataDispatch } = useContext(ritzDataContext);
    const loggedInAgent = selectLoggedInAgent(dataState) as Agent;
    const project = projectId ? selectProjectById(dataState, projectId) : undefined;
    const projectAgent = loggedInAgent ? selectProjectAgent(dataState, project?.id, loggedInAgent.id) : undefined;
    const projects = selectProjects(dataState);
    const drawerMenuOpened = selectDrawerMenuOpened(uiState);
    const ControlPanelComps = selectControlPanelComponents(uiState);
    const routeHelper = RouteHelper.getInstance(navigate);
    const { trans } = useRitzTranslation(project);
    const classes = useLayoutStyle();
    const { pathname } = location;
    const organization = getOrganization();


    const onDrawerMenuTogglerClicked = useCallback(() => {
        uiDispatch(openDrawerMenu());
    }, [uiDispatch]);


    const onGoHomeClicked = useCallback(() => {
        if (project) {
            routeHelper.gotoProjectHomePage(project.id);
        } else {
            routeHelper.gotoRitzHomePage();
        }
    }, [project, routeHelper]);



    const onProfileMenuItemClicked = useCallback(async (id: string) => {
        if (id === MenuItem.MY_ACCOUNT) {
            routeHelper.gotoMyAccountPage(project?.id);
        } else if (id === MenuItem.LOGOUT) {
            try {
                await dataDispatch(logoutAgent(), false);
                deleteLocalStorageItem(Constants.LS_KEY_LOGIN_INFO);
                routeHelper.gotoLoginPage();
            } catch (error) {
                /* ignore error */
            }
        }
    }, [routeHelper, project?.id, dataDispatch]);



    const onLoginClicked = useCallback(() => {
        routeHelper.gotoLoginPage();
    }, [routeHelper]);


    const onDrawerMenuItemClicked = useCallback((url: string) => {
        uiDispatch(closeDrawerMenu());
        navigate(url);
    }, [uiDispatch, navigate]);


    /**
     * Get the custom drawer menu based on the current project
     */
    const projectCustomDrawerMenu = useMemo(() => {
        const projectHandler = ProjectTypeHandlersManager.getInstance().getProjectCustomUIHandler(project?.supportToolSettings.projectType);
        if (projectHandler) {
            return projectHandler.getCustomUIConfig(loggedInAgent, project).drawerMenu;
        }
        return undefined;
    }, [loggedInAgent, project]);


    /**
     * Set up and cache the GLOBAL menu items that will be shown for all projects e.g. "My Projects", "My Accounts", etc... menu
     */
    const globalMenuItems = useMemo((): readonly DrawerMenuItem[] => {
        return [
            {
                disabled: false,
                label   : capitalizeAll(trans('drawer_menu.my_projects.label')),
                icon    : 'apps',
                link    : routeHelper.getRitzHomePageUrl(),
                selected: pathname === routeHelper.getRitzHomePageUrl(),
                hidden  : projects && projects.length === 1,
            },
            {
                disabled: false,
                label   : capitalizeAll(trans('drawer_menu.my_account.label')),
                icon    : 'account_circle',
                link    : routeHelper.getMyAccountPageUrl(project?.id),
                selected: pathname === routeHelper.getMyAccountPageUrl(project?.id),
            },
        ];
    }, [pathname, project?.id, projects, routeHelper, trans]);



    // Get the page info comp based on the current project's type
    const PageInfoBarComp = useMemo(() => {
        const pageLayoutHandler = ProjectTypeHandlersManager.getInstance().getPageLayoutHandler(project?.supportToolSettings.projectType);
        if (pageLayoutHandler && pageLayoutHandler.getPageInfoComponent) {
            return pageLayoutHandler.getPageInfoComponent(project, loggedInAgent) || PageInfoBarContainer;
        }

        return PageInfoBarContainer;
    }, [loggedInAgent, project]);



    /**
     * Set up and cache the list of visible menuItems based on the projectCustomDrawerMenu's menuItems array
     */
    const customMenuItems = useMemo((): readonly DrawerMenuItem[] => {
        return projectCustomDrawerMenu?.menuItems.filter((menuItem) => {
            // Filter out the main menu items that are NOT visible
            return isMenuItemVisible(menuItem, projectAgent?.role);
        }).map((menuItem): DrawerMenuItem => {
            return {
                disabled    : menuItem.disabled,
                label       : capitalizeAll(trans(menuItem.labelKey)),
                icon        : typeof menuItem.iconKey === 'string' ? trans(menuItem.iconKey) : menuItem.iconKey,
                link        : normalizeProjectMenuItemLink(menuItem.link, projectId, routeHelper, trans),
                selected    : false,
                hidden      : menuItem.hidden,
                subMenuItems: menuItem.subMenuItems?.filter((subMenuItem) => {
                    // Filter out the sub menu items that are NOT visible
                    return isMenuItemVisible(subMenuItem, projectAgent?.role);
                }).map((subMenuItem) => {
                    return {
                        label   : capitalizeAll(trans(subMenuItem.labelKey)),
                        icon    : typeof subMenuItem.iconKey === 'string' ? trans(subMenuItem.iconKey) : subMenuItem.iconKey,
                        link    : normalizeProjectMenuItemLink(subMenuItem.link, projectId, routeHelper, trans),
                        selected: false,
                        hidden  : subMenuItem.hidden,
                    };
                }),
                selectable: menuItem.selectable,
            };
        }) ?? [];
    }, [projectAgent?.role, projectCustomDrawerMenu?.menuItems, projectId, routeHelper, trans]);


    /**
     * Combine the global menu items with the customMenuItems. This will be the object we will be passing to the DrawerMenu
     */
    const combinedMenuItems = useMemo((): readonly (readonly DrawerMenuItem[])[] => {
        return [customMenuItems, globalMenuItems];
    }, [customMenuItems, globalMenuItems]);



    // If organization is undefined then that mean we weren't able to get organization based on baseUrl or orgId in the params
    if (!organization) {
        return <NotAuthorized />;
    }


    return (
        <div className={clsx(props.className, classes.root)} style={props.style}>
            {loggedInAgent && (
                <TopNavBar
                    className={clsx(props.topNavProps?.className, classes.topNavBar)}
                    style={props.topNavProps?.style}
                    title={project
                        ? project.name
                        : trans(organization.id === DEFAULT_ORGANIZATION_ID ? 'app.title_default' : 'app.title', { org_name: organization.name })}
                    profileMenu={loggedInAgent ? {
                        name     : loggedInAgent.name || loggedInAgent.email,
                        role     : projectAgent ? trans(getAgentRoleName(projectAgent.role)) : undefined,
                        avatar   : loggedInAgent.avatar,
                        menuItems: [{
                            id   : MenuItem.MY_ACCOUNT,
                            label: capitalizeAll(trans('label.my_account')),
                            icon : 'account_circle',
                        }, {
                            id   : MenuItem.LOGOUT,
                            label: capitalizeAll(trans('label.logout')),
                            icon : 'exit_to_app',
                        }],
                        onProfileMenuItemClicked,
                    } : undefined}
                    drawerMenuTogglerComp={(
                        <IconButton
                            color="inherit"
                            edge="start"
                            onClick={onDrawerMenuTogglerClicked}
                            className={classes.drawerMenuToggler}
                        >
                            <Icon>menu</Icon>
                        </IconButton>
                    )}
                    languageSelectorComp={<LanguageSelectorContainer projectId={projectId} />}
                    breadcrumbsBarComp={<PageInfoBarComp className={classes.pageInfoBar} />}
                    subRouteNavBarComp={<SubRouteNavBarContainer projectId={projectId} />}
                    controlBarComp={ControlPanelComps?.map((controlBarComp, index) => {
                        return (
                            // eslint-disable-next-line react/no-array-index-key
                            <Fragment key={index}>
                                {controlBarComp}
                            </Fragment>
                        );
                    })}
                    onGoHomeClicked={onGoHomeClicked}
                    loginLabel={trans('label.login')}
                    onLoginClicked={onLoginClicked}
                />
            )}

            {loggedInAgent && (
                <DrawerMenu
                    className={clsx(props.drawerMenuProps?.className, classes.drawerMenu)}
                    style={props.drawerMenuProps?.style}
                    appName={projectCustomDrawerMenu?.appNameKey !== undefined
                        ? trans(projectCustomDrawerMenu?.appNameKey, { org_name: organization.name }) : ''}
                    appLogo={typeof projectCustomDrawerMenu?.projectLogo === 'string'
                        ? <img src={projectCustomDrawerMenu.projectLogo} alt={projectCustomDrawerMenu.projectLogo} />
                        : undefined}
                    backgroundImage={projectCustomDrawerMenu?.backgroundImage}
                    projectName={project ? project.name : undefined}
                    open={drawerMenuOpened}
                    expandAllOnLoad={projectCustomDrawerMenu?.expandAllOnLoad ?? true}
                    collapsible={projectCustomDrawerMenu?.collapsible ?? true}
                    optimizeForMediumScreen={false}
                    menuItems={combinedMenuItems}
                    onMenuItemClicked={onDrawerMenuItemClicked}
                    onClose={() => {
                        uiDispatch(closeDrawerMenu());
                    }}
                />
            )}

            <div className={classes.mainContent}>
                { props.children }
            </div>
        </div>
    );
};
